import { type RouteProp, useRoute } from "@react-navigation/native";
import React, { useCallback } from "react";
import { View, StyleSheet } from "react-native";

import type { RootStackParamList } from "../../../navigation/types";
import UnifiedQuizRenderer from "../../../shared/ui/UnifiedQuizRenderer";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { useAutoNavigation } from "../hooks/useAutoNavigation";
import { useMultiplayerQuizController } from "../hooks/useMultiplayerQuizController";
import { usePollSessionCompletion } from "../hooks/usePollSessionCompletion";
import { useRoomStatusMonitor } from "../hooks/useRoomStatusMonitor";
import { useSessionLoader } from "../hooks/useSessionLoader";

type MultiplayerQuizRouteParams = RootStackParamList["MultiplayerQuiz"] & {
  reviewMode?: boolean;
  userId?: string;
  playerName?: string;
  difficulty?: string;
};

const MultiplayerQuizScreen = () => {
  const route = useRoute<RouteProp<{ MultiplayerQuiz: MultiplayerQuizRouteParams }, "MultiplayerQuiz">>();
  const { language } = useLanguageStore();

  const {
    sessionId,
    roomId,
    isHost = false,
    reviewMode = false,
    userId: routeUserId,
    playerName: routePlayerName,
    difficulty: routeDifficulty,
    familyId,
    familyName,
    friendsGroupId,
    friendsGroupName,
  } = route.params;

  const appLanguage = language || "en";

  const { state, handlers, derived } = useMultiplayerQuizController({
    sessionId,
    roomId,
    isHost,
    reviewMode,
    routeDifficulty,
    playerName: routePlayerName,
    userId: routeUserId,
  });

  const {
    loadedQuestions,
    roomLanguage,
    languageMismatch,
    isLoadingSessionData,
    sessionError,
    sessionStatus,
    reloadSession,
  } = useSessionLoader({
    sessionId,
    appLanguage,
  });

  React.useEffect(() => {
    if (sessionStatus === 'completed') {
      handlers.setCanNavigateToResults(true);
    }
  }, [sessionStatus, handlers]);

  React.useEffect(() => {
    if (loadedQuestions.length > 0 && !derived.isInitialized) {
      derived.localQuiz.initializeQuestions(loadedQuestions);
    }
  }, [loadedQuestions, derived.localQuiz, derived.isInitialized]);

  usePollSessionCompletion({
    sessionId,
    isNavigating: state.isNavigating,
    onComplete: () => {
      handlers.setCanNavigateToResults(true);
    },
  });

  useAutoNavigation({
    canNavigateToResults: state.canNavigateToResults,
    waitingScreenShownAt: state.waitingScreenShownAt,
    sessionId,
    roomId,
    isHost,
    playerName: derived.effectivePlayerName,
    familyId,
    familyName,
    friendsGroupId,
    friendsGroupName,
  });

  const handleServerGameEnded = useCallback(() => {
    if (state.canNavigateToResults) {
      return;
    }

    handlers.setCanNavigateToResults(true);
  }, [
    state.canNavigateToResults,
    handlers,
  ]);

  // CRITICAL: Monitor room status for server-side game ending
  useRoomStatusMonitor({
    roomId,
    onGameEnded: handleServerGameEnded,
  });

  return (
    <View style={styles.container}>
        <View style={styles.innerContainer}>
          <UnifiedQuizRenderer
            mode="multiplayer"
            isLoading={false}
            isGameFinished={derived.isGameFinished}
            error={null}
            questions={derived.questions}
            currentQuestion={derived.currentQuestion}
            selectedAnswer={state.selectedAnswer}
            showExplanation={state.showExplanation}
            onAnswerSelect={handlers.handleAnswerSelect}
            onNextQuestion={handlers.handleNextQuestion}
            currentIndex={derived.currentIndex}
            totalQuestions={derived.totalQuestions}
            score={derived.score || 0}
            correctCount={derived.correctCount || 0}
            timeLeft={derived.localQuiz.gameTimer}
            maxTime={derived.localQuiz.gameTimer}
            isLoadingSessionData={isLoadingSessionData}
            isNavigating={state.isNavigating}
            sessionError={sessionError}
            onRetryLoadSession={reloadSession}
            roomLanguage={roomLanguage}
            languageMismatch={languageMismatch}
            appLanguage={appLanguage}
            gracePeriodEnd={
              derived.gracePeriodEnd !== null
                ? derived.gracePeriodEnd.toString()
                : null
            }
            isSubmittingResults={state.isSubmittingResults}
            isReviewMode={reviewMode}
            isWaitingForResults={state.isWaitingForResults || sessionStatus === 'completed'}
            onReviewAnswers={() => {
              // TODO: Implement review mode logic in localQuiz if needed
              // derived.localQuiz.setReviewMode(true);
            }}
            onGoHome={() => {
              // TODO: Implement reset logic in localQuiz if needed
              // derived.localQuiz.reset();
            }}
            roomId={roomId}
            isPlayerAnswered={state.isAnswerSubmitted}
            onAutoSubmit={handlers.handleAutoSubmit}
            isHost={isHost}
            familyId={familyId}
            familyName={familyName}
            friendsGroupId={friendsGroupId}
            friendsGroupName={friendsGroupName}
          />
        </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAF8FF',
  },
  innerContainer: {
    flex: 1,
  },
});

export default MultiplayerQuizScreen;
